import { Accessor, Setter, createContext, createMemo, createSignal, useContext } from "solid-js";
import { StepContextJsx } from './partials/Step'
import { requireContext } from "common";

export type BeforeLeaveHandler = (args: { from: StepContext, to: StepContext }) => boolean | Promise<boolean>;
export type NextReturn = boolean | undefined | void;
export type OnNextHandler = () => NextReturn | Promise<NextReturn>;

export class StepContext {
    id: number;
    title: string;
    wizardCtx: WizardContext;

    isVisible: () => boolean;
    completed: Accessor<boolean>;
    setCompleted: Setter<boolean>;
    onNext?: OnNextHandler;

    constructor(wizardCtx: WizardContext, id: number, title: string, isVisible: () => boolean, onNext?: OnNextHandler) {
        this.id = id;
        this.title = title;
        this.wizardCtx = wizardCtx;
        this.isVisible = isVisible;
        this.onNext = onNext;

        [this.completed, this.setCompleted] = createSignal(false);
    }

    next() {
        this.setCompleted(true);
        this.wizardCtx.selectNextStep();
    }
}

export class WizardContext {
    steps: Accessor<StepContext[]>;
    setSteps: Setter<StepContext[]>;

    currStep: Accessor<number>;
    setCurrStep: Setter<number>;

    hasOnComplete: boolean;
    onComplete?: () => void;
    onCancel?: () => void;

    size: Accessor<'small' | 'medium'>;

    constructor(onComplete?: () => void, onCancel?: () => void, size?: Accessor<'small' | 'medium'>, initialStep?: number) {
        this.size = createMemo(() => size ? size() : 'medium');

        this.hasOnComplete = !!onComplete;
        this.onComplete = async () => {
            const step = this.steps()[this.currStep()];
            const canMove = step?.onNext ? await step.onNext() : true;
            if (canMove === false) return;

            onComplete?.();
        }

        this.onCancel = onCancel;

        [this.steps, this.setSteps] = createSignal<StepContext[]>([]);
        [this.currStep, this.setCurrStep] = createSignal(initialStep ?? 0);
    }

    addStep(title: string, visible?: () => boolean, onNext?: OnNextHandler): StepContext {
        const existingStep = this.steps().find(s => s.title === title);
        if (existingStep) return existingStep;

        const ctx = new StepContext(this, this.steps().length, title, visible ?? (() => true), onNext);
        this.setSteps([...this.steps(), ctx]);

        return ctx;
    }

    validSteps() {
        return this.steps()
            .filter(s => s.isVisible());
    }

    async selectNextStep() {
        const from = this.steps()[this.currStep()];
        if (!from?.completed()) return;

        const canStep = from.onNext ? await from.onNext() : true;
        if (canStep === false) return;

        const to = this.nextValidStep();
        if (to) {
            this.setCurrStep(to.id);
        }
    }

    selectPrevStep() {
        const step = this.steps()[this.currStep()];
        if (this.hasPreviousStep()) {
            this.setCurrStep(this.prevValidStep()!.id);
        }
    }

    nextValidStep() {
        let step = this.currStep() + 1;
        while (step < this.steps().length && !this.steps()[step].isVisible()) {
            step++;
        }

        if (step < this.steps().length) {
            return this.steps()[step];
        }
    }

    prevValidStep() {
        let step = this.currStep() - 1;
        while (step > 0 && !this.steps()[step].isVisible()) {
            step--;
        }

        if (step < this.steps().length) {
            return this.steps()[step];
        }
    }

    hasPreviousStep() {
        return this.validSteps()[0]?.id !== this.currStep();
    }

    hasNextStep() {
        return !!this.nextValidStep();
    }
}

export const WizardContextJsx = createContext<WizardContext>();

export const useWizardContext = () => {
    return useContext(WizardContextJsx);
}

export const requireWizardContext = () => {
    return requireContext(WizardContextJsx);
}

export const requireStepContext = () => {
    return requireContext(StepContextJsx);
};
